import React, { useEffect, useState } from 'react';
import axios from 'axios';
import Layout from '@/components/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { Calendar } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AdminAttendance() {
  const [attendance, setAttendance] = useState([]);
  const [filteredAttendance, setFilteredAttendance] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchAttendance();
  }, []);

  useEffect(() => {
    if (searchTerm) {
      const filtered = attendance.filter(
        (att) =>
          att.employee_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          att.employee_email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          att.date?.includes(searchTerm)
      );
      setFilteredAttendance(filtered);
    } else {
      setFilteredAttendance(attendance);
    }
  }, [searchTerm, attendance]);

  const fetchAttendance = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/attendance/all`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAttendance(response.data);
      setFilteredAttendance(response.data);
    } catch (error) {
      toast.error('Failed to load attendance');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const variants = {
      present: 'default',
      absent: 'destructive',
      late: 'secondary',
      holiday: 'outline'
    };
    return <Badge variant={variants[status] || 'default'}>{status.toUpperCase()}</Badge>;
  };

  return (
    <Layout>
      <div className="space-y-8" data-testid="admin-attendance-page">
        <div>
          <h1 className="text-4xl font-bold tracking-tight">Attendance Management</h1>
          <p className="text-muted-foreground mt-2">View and manage employee attendance</p>
        </div>

        <Card data-testid="attendance-table-card">
          <CardHeader>
            <CardTitle className="flex items-center">
              <Calendar className="mr-2 h-5 w-5" />
              All Attendance Records
            </CardTitle>
            <CardDescription>Complete attendance history of all employees</CardDescription>
            <div className="pt-4">
              <Input
                placeholder="Search by name, email, or date..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                data-testid="search-input"
              />
            </div>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-8">Loading...</div>
            ) : filteredAttendance.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                {searchTerm ? 'No matching records found' : 'No attendance records found'}
              </div>
            ) : (
              <div className="w-full overflow-auto rounded-lg border border-border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Employee Name</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Check In</TableHead>
                      <TableHead>Check Out</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredAttendance.map((record) => (
                      <TableRow key={record.id} data-testid="attendance-row">
                        <TableCell className="font-medium">{record.employee_name}</TableCell>
                        <TableCell>{record.employee_email}</TableCell>
                        <TableCell>{record.date}</TableCell>
                        <TableCell>{getStatusBadge(record.status)}</TableCell>
                        <TableCell>{record.check_in_time || '-'}</TableCell>
                        <TableCell>{record.check_out_time || '-'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
